import type { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';

import { tags } from '~features/tags/tagNames';
import ClientLogger from '~logger/client';
import { LoggerMessages, LoggerServices } from '~logger/constants';
import { api, apiTags } from '~shared/api/origin';
import { eventsInbox, EventType } from '~shared/services/eventsInbox';
import { getLanguageAgnosticHref } from '~shared/utils/getLanguageAgnosticHref';

import type {
	GetUserFeedbackApiResponse,
	GetUserFeedbackApiResponseTransformed,
	SendUserFeedbackApiRequest,
	TPublicProfile,
	TUserConfig,
	TUserExperiments,
	TUserInfo,
	TUserLevel,
	TUserProfile,
	TUserProfilePreferences,
	TUserTags,
} from './types';

const usersApi = api.injectEndpoints({
	endpoints: (build) => ({
		userInfo: build.query<TUserInfo, { id: string }>({
			query: ({ id }) => ({ url: `/users/private/${id}` }),
		}),
		userProfile: build.query<TUserProfile, { userId: string }>({
			query: ({ userId }) => ({ url: `ai/users/${userId}/profile` }),
		}),
		setUserProfile: build.mutation<{}, { userId: string; data: TUserProfile }>({
			query: ({ userId, data }) => ({
				url: `ai/users/${userId}/profile`,
				method: 'PUT',
				body: data,
			}),
			async onQueryStarted({ userId, data }, { dispatch, queryFulfilled }) {
				const infoPatch = dispatch(
					util.updateQueryData('userProfile', { userId }, (draft) => {
						draft.bio = data.bio;
						draft.nick = data.nick;
					})
				);

				const infoPatch2 = dispatch(
					util.updateQueryData('userInfo', { id: userId }, (draft) => {
						if (data.nick) draft.nick = data.nick;
					})
				);

				const infoPatch3 = dispatch(
					util.updateQueryData(
						'userPublicProfile',
						{ nickname: data.nick },
						(draft) => {
							if (data.bio) draft.Bio = data.bio ?? '';
						}
					)
				);

				try {
					await queryFulfilled;
				} catch (e) {
					ClientLogger.error(
						LoggerServices.UserService,
						LoggerMessages.UserInfoUpdateError,
						{ error: e }
					);
					infoPatch.undo();
					infoPatch2.undo();
					infoPatch3.undo();
				}
			},
		}),
		userKinks: build.query<
			{ kinks: { id: string; text: string }[] },
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/bots/generation/${userId}/settings/kinks`,
			}),
		}),
		userProfilePreferences: build.query<
			{ preferences: TUserProfilePreferences },
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/users/${userId}/profile/preferences`,
			}),
		}),
		setUserProfilePreferences: build.mutation<
			{},
			{ userId: string; data: TUserProfilePreferences }
		>({
			query: ({ userId, data }) => ({
				url: `ai/users/${userId}/profile/preferences`,
				method: 'PUT',
				body: data,
			}),
			async onQueryStarted({ userId, data }, { dispatch, queryFulfilled }) {
				const infoPatch = dispatch(
					util.updateQueryData(
						'userProfilePreferences',
						{ userId },
						(draft) => {
							draft.preferences.genders = data.genders;
							draft.preferences.ageMin = data.ageMin;
							draft.preferences.ageMax = data.ageMax;
							draft.preferences.kinks = data.kinks;
						}
					)
				);

				try {
					await queryFulfilled;
				} catch (e) {
					ClientLogger.error(
						LoggerServices.UserService,
						LoggerMessages.UserInfoUpdateError,
						{ error: e }
					);
					infoPatch.undo();
				}
			},
		}),
		userPublicProfile: build.query<TPublicProfile, { nickname: string }>({
			query: ({ nickname }) => ({
				url: `ai/users/${nickname}/profile/public`,
			}),
		}),
		userPreferences: build.query<{ language?: string }, { userId: string }>({
			query: ({ userId }) => ({ url: `ai/users/${userId}/preferences` }),
		}),
		setUserLanguage: build.mutation<
			unknown,
			{ userId: string; language: string }
		>({
			query: ({ userId, language }) => ({
				url: `ai/users/${userId}/preferences`,
				method: 'PATCH',
				body: {
					language,
				},
			}),
		}),
		editUserInfo: build.mutation<
			undefined,
			{
				id: string;
				data: Pick<TUserInfo, 'gender' | 'name' | 'birthday'>;
			}
		>({
			query: ({ id, data }) => ({
				url: `/users/${id}`,
				method: 'PATCH',
				body: data,
			}),
			async onQueryStarted({ id, data }, { dispatch, queryFulfilled }) {
				const userInfoReq = dispatch(
					usersApi.endpoints.userInfo.initiate({ id }, { subscribe: false })
				);
				try {
					await userInfoReq.unwrap();
				} catch {}

				const infoPatch = dispatch(
					util.updateQueryData('userInfo', { id }, (draft) => {
						draft.gender = data.gender;
						draft.name = data.name;
						draft.birthday = data.birthday;
					})
				);
				try {
					await queryFulfilled;
				} catch (e) {
					ClientLogger.error(
						LoggerServices.UserService,
						LoggerMessages.UserInfoUpdateError,
						{ error: e }
					);
					infoPatch.undo();
				}
			},
		}),
		tags: build.query<TUserTags, { id: string }>({
			query: ({ id }) => ({ url: `/users/${id}/tags` }),
			async onCacheEntryAdded(
				{},
				{ updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch }
			) {
				// create subscription
				let unsub = null;
				try {
					await cacheDataLoaded;

					unsub = eventsInbox.onMsg((msg) => {
						switch (msg.type) {
							case EventType.UserTagSet:
								const tag = msg.payload.tag;
								updateCachedData((draft) => {
									draft.push(tag);
								});
								if (tag === tags.subscription) {
									dispatch(
										api.util.invalidateTags([
											apiTags.subscriptions,
											apiTags.characterGenerationQuota,
											apiTags.imagesGenerationQuota,
											apiTags.videosGenerationQuota,
											apiTags.generatedMedia,
											apiTags.generatedPublicMedia,
											apiTags.generatedSimilarMedia,
											apiTags.generatedLikedMedia,
											apiTags.generatedUnlockedMedia,
											apiTags.gifts,
											apiTags.userConfig,
										])
									);
								}
								break;

							case EventType.UserTagUnset:
								updateCachedData((draft) => {
									const tag = msg.payload.tag;
									const indexOfTag = draft.indexOf(tag);

									if (indexOfTag !== -1) {
										draft.splice(indexOfTag, 1);
									}
								});
								break;

							// tags and experiments are concatenated via useUserTags hook
							case EventType.UserExperimentSet:
								const experimentTag = msg.payload.tag;
								updateCachedData((draft) => {
									if (!draft.includes(experimentTag)) {
										draft.push(experimentTag);
									}
								});
								break;
						}
					});
				} catch (err) {}

				await cacheEntryRemoved;
				unsub?.();
			},
			providesTags: [apiTags.tags],
		}),
		experiments: build.query<TUserExperiments, { id: string }>({
			query: ({ id }) => ({ url: `/users/${id}/tags/experiments` }),
			providesTags: [apiTags.tags],
		}),
		userConfig: build.query<TUserConfig, { userId: string }>({
			query: ({ userId }) => ({ url: `ai/config/${userId}` }),
			providesTags: [apiTags.userConfig],
		}),
		setLastBotId: build.mutation<
			TUserConfig,
			{ userId: string; lastBotId: string }
		>({
			query: ({ userId, lastBotId }) => ({
				url: `ai/users/${userId}/props`,
				method: 'PATCH',
				body: {
					lastBotChatId: lastBotId,
				},
			}),
		}),
		setDiscordId: build.mutation<undefined, { userId: string; code: string }>({
			query: ({ userId, code }) => ({
				url: `ai/users/${userId}/discord/connect`,
				method: 'PATCH',
				headers: {
					'x-redirect-url': getLanguageAgnosticHref(),
				},
				body: {
					code,
				},
			}),
			async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
				const tagsReq = dispatch(
					usersApi.endpoints.tags.initiate({ id: userId }, { subscribe: false })
				);
				try {
					await tagsReq.unwrap();
				} catch {}
				const patch = dispatch(
					util.updateQueryData('tags', { id: userId }, (draft) => {
						if (!draft.includes(tags.discordConnected)) {
							draft.push(tags.discordConnected);
						}
					})
				);
				try {
					await queryFulfilled;
				} catch {
					patch.undo();
				}
			},
		}),
		getLevel: build.query<TUserLevel, { userId: string }>({
			async queryFn({ userId }, _queryApi, _extraOptions, fetchWithBQ) {
				const response = await fetchWithBQ(`/ai/users/${userId}/experience`);
				if (response.error && response.error.status !== 400)
					return {
						error: response?.error,
					};
				const data = (response?.data || null) as TUserLevel;
				return {
					data,
				};
			},
		}),
		userWelcomeMessage: build.mutation<
			undefined,
			{ userId: string; botId: string | number }
		>({
			query: ({ userId, botId }) => ({
				url: `ai/chats/${userId}/${botId}/init`,
				method: 'POST',
			}),
			invalidatesTags: [apiTags.chats],
		}),
		userDelete: build.mutation<unknown, { userId: string }>({
			query: ({ userId }) => ({
				url: `/users/${userId}`,
				method: 'DELETE',
			}),
		}),
		// sets order of bots to unlock on web
		setSelectedBot: build.mutation<unknown, { id: string }>({
			query: ({ id }) => ({
				url: `ai/users/${id}/onboarding-answers`,
				method: 'PATCH',
				body: {
					selectedBot: 'anna',
				},
			}),
		}),
		// sets default bot preferences during register
		setUserDefaultPreferences: build.mutation<unknown, { id: string }>({
			query: ({ id }) => ({
				url: `ai/users/${id}/preferences`,
				method: 'PATCH',
				body: {
					personality: 'flirty',
					birthday: '1999-12-31T23:00:00Z',
					name: 'Jessica',
					gender: 'fem',
					ethnicity: 'caucasian',
				},
			}),
		}),
		// feedback
		getUserFeedback: build.query<
			GetUserFeedbackApiResponseTransformed,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/users/${userId}/feedback`,
			}),
			transformResponse: (response: GetUserFeedbackApiResponse) => {
				const data = {} as GetUserFeedbackApiResponseTransformed;
				response.categories.forEach((feedback) => {
					data[feedback.category] = feedback;
				});

				return data;
			},
		}),
		sendUserFeedback: build.mutation<
			undefined,
			{ userId: string; body: SendUserFeedbackApiRequest }
		>({
			query: ({ userId, body }) => ({
				url: `ai/users/${userId}/feedback`,
				method: 'POST',
				body,
			}),
			async onQueryStarted({ userId, body }, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;
					dispatch(
						util.updateQueryData('getUserFeedback', { userId }, (draft) => {
							const current = draft[body.category];
							if (current) {
								current.feedbackCount += 1;
							} else {
								draft[body.category] = {
									category: body.category,
									feedbackCount: 1,
									latestIteration: NaN,
								};
							}
						})
					);
				} catch {}
			},
		}),
		// registration props
		getRegistrationProps: build.query<
			{ location?: string },
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/users/${userId}/registration/props`,
			}),
			providesTags: [apiTags.userProps],
		}),
		updateRegistrationProps: build.mutation<
			{ result: 'success' },
			{ userId: string; force?: boolean; location?: string }
		>({
			queryFn: async (
				{ userId, location, force },
				_queryApi,
				_extraOptions,
				fetchWithBQ
			) => {
				_queryApi.dispatch(api.util.invalidateTags([apiTags.userProps]));
				const result = await _queryApi.dispatch(
					usersApi.endpoints.getRegistrationProps.initiate({ userId })
				);

				if (force || (result.isSuccess && result.data.location === undefined)) {
					const { error } = await fetchWithBQ({
						url: `ai/users/${userId}/registration/props`,
						method: 'PATCH',
						body: { location },
					});

					if (error) {
						return {
							error: {
								status: error.status,
								data: error.data,
							} as FetchBaseQueryError,
						};
					}
				}

				return { data: { result: 'success' } };
			},
		}),
	}),
});

export const {
	util,
	endpoints,
	useUserProfileQuery,
	useUserProfilePreferencesQuery,
	useUserPublicProfileQuery,
	useUserPreferencesQuery,
	useUserKinksQuery,
	useSetUserLanguageMutation,
	useUserInfoQuery,
	useEditUserInfoMutation,
	useTagsQuery,
	useExperimentsQuery,
	useUserConfigQuery,
	useSetUserProfileMutation,
	useSetUserProfilePreferencesMutation,
	useGetLevelQuery,
	useUserWelcomeMessageMutation,
	useUserDeleteMutation,
	useSetSelectedBotMutation,
	useSetUserDefaultPreferencesMutation,
	useLazyUserPreferencesQuery,
	useSetLastBotIdMutation,
	useSetDiscordIdMutation,
	//feedback
	useGetUserFeedbackQuery,
	useSendUserFeedbackMutation,
	// registration props
	useGetRegistrationPropsQuery,
	useLazyGetRegistrationPropsQuery,
	useUpdateRegistrationPropsMutation,
} = usersApi;
